import type { GameState } from './types';
import type { Position, Stats } from '../types';
import { LABOR_DAILY_BASE } from '../constants';
import { todayStr } from '../utils/format';
import { storage } from '../sdk/storage';

const KEYS = {
  cash: 'chungsan_cash',
  pos: 'chungsan_pos',
  tut: 'chungsan_tut',
  dis: 'chungsan_dis',
  ad: 'chungsan_ad',
  boost: 'chungsan_boost',
  stats: 'chungsan_stats',
  pre: 'chungsan_pre',
  labor: 'chungsan_labor',
} as const;

export async function loadPersisted(): Promise<Partial<GameState>> {
  const today = todayStr();
  const out: Partial<GameState> = {};
  try {
    const cash = await storage.getItem(KEYS.cash);
    if (cash !== null) out.cash = +cash;

    const pos = await storage.getItem(KEYS.pos);
    if (pos && pos !== 'null') out.position = JSON.parse(pos) as Position;

    const tut = await storage.getItem(KEYS.tut);
    if (tut !== null) out.tutDone = tut === 'true';

    const dis = await storage.getItem(KEYS.dis);
    if (dis !== null) out.disclaimerDone = dis === 'true';

    const ad = await storage.getItem(KEYS.ad);
    if (ad) {
      const p = JSON.parse(ad) as { date: string; count: number };
      out.adDate = p.date === today ? p.date : today;
      out.adCount = p.date === today ? p.count : 0;
    } else {
      out.adDate = today;
    }

    const boost = await storage.getItem(KEYS.boost);
    if (boost !== null) out.boostActive = boost === 'true';

    const stats = await storage.getItem(KEYS.stats);
    if (stats) out.stats = JSON.parse(stats) as Stats;

    const pre = await storage.getItem(KEYS.pre);
    if (pre) out.presets = JSON.parse(pre) as [number, number, number];

    const labor = await storage.getItem(KEYS.labor);
    if (labor) {
      const p = JSON.parse(labor) as { date: string; used: number; max: number };
      if (p.date === today) {
        out.laborDate = p.date;
        out.laborUsed = p.used;
        out.laborMax = p.max;
      } else {
        out.laborDate = today;
        out.laborUsed = 0;
        out.laborMax = LABOR_DAILY_BASE;
      }
    } else {
      out.laborDate = today;
    }
  } catch {
    /* storage 접근 실패 — 무시 */
  }
  return out;
}

// fire-and-forget 비동기 — 호출자는 await 불필요
export function persist(s: GameState): void {
  void (async () => {
    try {
      await Promise.all([
        storage.setItem(KEYS.cash, String(s.cash)),
        storage.setItem(KEYS.pos, JSON.stringify(s.position)),
        storage.setItem(KEYS.tut, String(s.tutDone)),
        storage.setItem(KEYS.dis, String(s.disclaimerDone)),
        storage.setItem(KEYS.ad, JSON.stringify({ date: s.adDate, count: s.adCount })),
        storage.setItem(KEYS.boost, String(s.boostActive)),
        storage.setItem(KEYS.stats, JSON.stringify(s.stats)),
        storage.setItem(KEYS.pre, JSON.stringify(s.presets)),
        storage.setItem(
          KEYS.labor,
          JSON.stringify({ date: s.laborDate, used: s.laborUsed, max: s.laborMax }),
        ),
      ]);
    } catch {
      /* noop */
    }
  })();
}
